import React, { useState, useEffect } from "react";

import campaignStepOneIcon from "../../assets/images/svg/mab-compgn-6.svg";
import dropOpenIcon from "../../assets/images/svg/mab-drop-open-6.svg";
import downArrow from "../../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../../assets/images/svg/mab-ellipse.svg";
import NextButton from "../common/next-button";

type AdvancedSettingsDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onNext?: (id: string) => void;
  onAgeChange?: (value: string) => void;
  onGenderChange?: (value: string) => void;
  onAdTextChange?: (value: string) => void;
  onAdHeadlineChange?: (value: string) => void;
  isEditMode?: boolean;
  initialAge?: number | null; // age_min from draft
  initialMaxAge?: number | null; // age_max from draft
  initialGender?: number | null; // 1 for male, 2 for female from draft
  initialAdText?: string; // ad text message from draft
  initialAdHeadline?: string; // ad headline from draft
};

const AGE_OPTIONS = [
  { id: "all", label: "All age" },
  { id: "18-25", label: "18-25" },
  { id: "25-45", label: "25-45" },
  { id: "45-90+", label: "45-90+" },
];

const GENDER_OPTIONS = [
  { id: "all", label: "All" },
  { id: "male", label: "Male" },
  { id: "female", label: "Female" },
  { id: "other", label: "Other" },
];

const AdvancedSettingsDropdown: React.FC<AdvancedSettingsDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  onNext,
  onAgeChange,
  onGenderChange,
  onAdTextChange,
  onAdHeadlineChange,
  isEditMode = false,
  initialAge,
  initialMaxAge,
  initialGender,
  initialAdText,
  initialAdHeadline,
}) => {
  const isOpen = expandedId === id;
  const [ageRange, setAgeRange] = useState<string>("all");
  const [gender, setGender] = useState<string>("all");
  const [adText, setAdText] = useState<string>("");
  const [adHeadline, setAdHeadline] = useState<string>("Listen now on Spotify!");

  // Initialize values from props when draft data is available
  useEffect(() => {
    if (initialAge !== undefined && initialAge !== null && initialMaxAge !== undefined && initialMaxAge !== null) {
      // Find matching age range
      if (initialAge === 18 && initialMaxAge === 65) {
        setAgeRange("all");
        onAgeChange?.("All age");
      } else if (initialAge === 18 && initialMaxAge === 25) {
        setAgeRange("18-25");
        onAgeChange?.("18-25");
      } else if (initialAge === 25 && initialMaxAge === 45) {
        setAgeRange("25-45");
        onAgeChange?.("25-45");
      } else if (initialAge === 45 && initialMaxAge === 90) {
        setAgeRange("45-90+");
        onAgeChange?.("45-90+");
      } else {
        // Custom range - try to find closest match or set to all
        if (initialAge === 18 && initialMaxAge === 65) {
          setAgeRange("all");
          onAgeChange?.("All age");
        }
      }
    }

    if (initialGender !== undefined && initialGender !== null) {
      if (initialGender === 1) {
        setGender("male");
        onGenderChange?.("Male");
      } else if (initialGender === 2) {
        setGender("female");
        onGenderChange?.("Female");
      } else {
        setGender("all");
        onGenderChange?.("All");
      }
    }

    if (initialAdText !== undefined && initialAdText !== null) {
      setAdText(initialAdText);
      onAdTextChange?.(initialAdText);
    }

    if (initialAdHeadline !== undefined && initialAdHeadline !== null) {
      setAdHeadline(initialAdHeadline);
      onAdHeadlineChange?.(initialAdHeadline);
    }
  }, [initialAge, initialMaxAge, initialGender, initialAdText, initialAdHeadline, onAgeChange, onGenderChange, onAdTextChange, onAdHeadlineChange]);

  // Initialize default adHeadline value on mount (only if no initial value)
  useEffect(() => {
    if (initialAdHeadline === undefined || initialAdHeadline === null) {
      onAdHeadlineChange?.("Listen now on Spotify!");
    }
  }, [onAdHeadlineChange, initialAdHeadline]);

  return (
    <div className="w-full">
  <div
    className="relative rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] overflow-hidden"
    style={{
      borderRadius: "20px",
      background: "#878DA11A",
      backgroundImage: `url(${ellipseBg})`,
      backgroundSize: "100% 100%",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    }}
  >
    {/* Header */}
    <div
      className="flex items-center gap-3 cursor-pointer hover:opacity-90 transition-opacity"
      style={{ height: "88px", padding: "24px" }}
      onClick={() => onToggle(id)}
    >
      <div className="flex items-center gap-3 flex-1">
        <img
          src={isOpen ? dropOpenIcon : campaignStepOneIcon}
          alt="Step 9"
          className="w-8 h-8"
        />
        <span
          style={{
            fontFamily: "Poppins",
            fontWeight: 400,
            fontSize: "20px",
            lineHeight: "130%",
            color: isOpen ? "#58F2A9" : "#FFFFFF",
          }}
        >
          Advanced Settings
        </span>
      </div>

      <img
        src={downArrow}
        alt="Toggle"
        className="pointer-events-none transition-transform"
        style={{
          height: "9.5px",
          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          filter: isOpen
            ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
            : "none",
        }}
      />
    </div>

    {isOpen && (
      <div className="pb-6" style={{ paddingLeft: "24px", paddingRight: "24px" }}>
        {/* 🔥 ALIGNMENT FIX ONLY */}
        <div className="ml-[41px] flex flex-col gap-6">

          {/* Age range */}
          <div className="flex flex-col gap-3 w-full items-start">
            <span className="text-white text-[16px] font-medium">
              Age range from
            </span>

            <div
              className="bg-[#1B2731] py-2 flex flex-wrap items-center justify-between w-full"
              style={{
                borderRadius: "99px",
                paddingLeft: "12px",
                paddingRight: "12px",
                opacity: isEditMode ? 0.6 : 1,
              }}
            >
              {AGE_OPTIONS.map((opt) => {
                const active = ageRange === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      if (!isEditMode) {
                        setAgeRange(opt.id);
                        onAgeChange?.(opt.label);
                      }
                    }}
                    disabled={isEditMode}
                    className={`text-[16px] font-medium flex items-center justify-center ${
                      active
                        ? "bg-white text-[#0B0D12]"
                        : "bg-transparent text-white hover:opacity-80"
                    }`}
                    style={{
                      borderRadius: "99px",
                      flex: 1,
                      height: "49.6px",
                      background: active ? "white" : "transparent",
                      border: "none",
                      cursor: isEditMode ? "not-allowed" : "pointer",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="border-b border-[#1F2733] mt-1" />
          </div>

          {/* Gender */}
          <div className="flex flex-col gap-3 w-full items-start">
            <span className="text-white text-[16px] font-normal">
              Gender
            </span>

            <div className="flex flex-col gap-3 w-full">
              {GENDER_OPTIONS.map((opt) => {
                const active = gender === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      if (!isEditMode) {
                        setGender(opt.id);
                        onGenderChange?.(opt.label);
                      }
                    }}
                    disabled={isEditMode}
                    className="flex items-center gap-3 text-white text-[16px]"
                    style={{
                      opacity: isEditMode ? 0.6 : 1,
                      cursor: isEditMode ? "not-allowed" : "pointer",
                    }}
                  >
                    <span
                      className={`inline-flex h-5 w-5 rounded-full border-2 ${
                        active
                          ? "border-[#58F2A9] bg-[#58F2A9]"
                          : "border-[#878DA199]"
                      }`}
                    />
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="border-b border-[#1F2733] mt-1" />
          </div>

          {/* Ad Text */}
          <div className="flex flex-col gap-2">
            <span className="text-white text-[16px]">
              Ad Text
            </span>

            <input
              type="text"
              value={adText}
              onChange={(e) => {
                if (!isEditMode && e.target.value.length <= 300) {
                  setAdText(e.target.value);
                  onAdTextChange?.(e.target.value);
                }
              }}
              maxLength={300}
              placeholder="Enter text to override headline generated..."
              disabled={isEditMode}
              className="bg-white rounded-[12px] w-full text-[#020202] text-[16px] px-4 py-4 focus:outline-none focus:ring-2 focus:ring-[#58F2A9]"
              style={{
                opacity: isEditMode ? 0.6 : 1,
                backgroundColor: isEditMode ? "#f0f0f0" : "white",
                cursor: isEditMode ? "not-allowed" : "text",
              }}
            />
          </div>

          {/* Ad Headline */}
          <div className="flex flex-col gap-2">
            <span className="text-white text-[16px]">
              Ad Headline
            </span>

            <input
              type="text"
              value={adHeadline}
              onChange={(e) => {
                if (!isEditMode && e.target.value.length <= 40) {
                  setAdHeadline(e.target.value);
                  onAdHeadlineChange?.(e.target.value);
                }
              }}
              maxLength={40}
              placeholder="Enter text to override bottom headline generated...."
              disabled={isEditMode}
              className="bg-white rounded-[12px] w-full text-[#020202] text-[16px] px-4 py-4 focus:outline-none focus:ring-2 focus:ring-[#58F2A9]"
              style={{
                opacity: isEditMode ? 0.6 : 1,
                backgroundColor: isEditMode ? "#f0f0f0" : "white",
                cursor: isEditMode ? "not-allowed" : "text",
              }}
            />
          </div>

          {/* Next button */}
          <div className="flex w-full justify-end">
            <NextButton size="sm" onClick={() => onNext?.(id)} />
          </div>

        </div>
      </div>
    )}
  </div>
</div>

  );
};

export default AdvancedSettingsDropdown;

